/**
 * Discard Draft Dialog Component
 * @implements FS-002.6.4 - Discard Draft Changes
 * @implements TS-075 - Control State UI Components
 *
 * Confirmation dialog shown before discarding draft changes.
 * Warns the user that all draft changes will be lost.
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

/**
 * Props for DiscardDraftDialog component
 */
export interface DiscardDraftDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed (cancel) */
  onClose: () => void;
  /** Callback when the user confirms discarding */
  onConfirm: () => void;
  /** Whether the discard action is in progress */
  isLoading?: boolean;
}

/**
 * Discard Draft Dialog Component
 * @implements TS-075 - Control State UI Components
 *
 * Displays a confirmation dialog before discarding draft changes.
 * Provides clear warning about data loss.
 *
 * @example
 * ```tsx
 * <DiscardDraftDialog
 *   open={dialogOpen}
 *   onClose={() => setDialogOpen(false)}
 *   onConfirm={handleDiscard}
 *   isLoading={isDiscarding}
 * />
 * ```
 */
export const DiscardDraftDialog = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
}: DiscardDraftDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      aria-labelledby="discard-draft-dialog-title"
      aria-describedby="discard-draft-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        id="discard-draft-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'warning.main',
        }}
      >
        <WarningIcon color="warning" />
        Discard Draft Changes?
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="discard-draft-dialog-description">
          Are you sure you want to discard all draft changes? This action cannot
          be undone and all changes made since entering draft mode will be lost.
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          color="error"
          variant="contained"
          autoFocus
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Discard Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
